import { DetailBreadcrumb } from "../components/DetailBreadcrumb";
import { ClosedPostNotice } from "../components/ClosedPostNotice";
import { useEffect, useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { ArrowRight, Building2, MapPin, MessageSquare, Package, ReceiptText, Star, Users, Wrench } from "lucide-react";
import { LocationPin } from "../components/LocationPin";
import { Rating } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { setMeta, resetMeta } from "../lib/seo";
import { companies as mockCompanies, peso, type Company } from "../data/mock";
import { SaveButton } from "../components/SaveButton";
import { MobileActionBar } from "../components/MobileActionBar";
import { useAuth } from "../lib/auth";
import { messagesHref } from "../lib/actions";
import { CoverArt } from "../components/cards";
import { useInPageActionVisibility } from "../lib/useInPageActionVisibility";
import { ImageLightbox, type LightboxImage } from "../components/ImageLightbox";
import { CompanyDetailMobile } from "../components/CompanyDetailMobile";
import { companyCoverImage } from "../lib/companyCover";

export type CompanyStoreService = {
  id: string;
  title: string;
  by: string;
  company?: boolean;
  city: string;
  from: number;
  rating?: number;
  reviews?: number;
  tags: string[];
  verified?: boolean;
  coverImage?: string | null;
};

export type CompanyStoreItem = {
  id: string;
  title: string;
  kind: string;
  mode: string;
  price: number;
  unit?: string;
  condition?: string;
  location?: string;
  category?: string;
  tags: string[];
  stock?: number;
  imageUrl?: string | null;
  coverImage?: string | null;
};

export type PublicCompany = Company & {
  ownerUserId?: number | null;
  logoObject?: string | null;
  coverObject?: string | null;
  primaryIndustry?: string | null;
  psicCode?: string | null;
  subtype?: string | null;
  ownershipClassifications?: string[];
  enterpriseClassifications?: string[];
  corporateRelationship?: string | null;
  operatingStatus?: string | null;
  profileRepresents?: string | null;
  serviceCoverage?: string | null;
  businessRoles?: string[];
  secondaryIndustries?: string[];
  productsServices?: string[];
  customerMarkets?: string[];
};

export default function CompanyDetail() {
  const { t } = useI18n();
  const [, params] = useRoute("/c/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const { item: c, state } = useListing("companies", id, mockCompanies);
  // v3.9.65 (Stage 6 / I) — the company's storefront: their items + services.
  const [store, setStore] = useState<{ items: CompanyStoreItem[]; services: CompanyStoreService[] }>({ items: [], services: [] });
  const [activeSection, setActiveSection] = useState("overview");
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  const { actionRef, isVisible } = useInPageActionVisibility<HTMLDivElement>(id);

  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobileViewport(viewport.matches);
    update();
    viewport.addEventListener("change", update);
    return () => viewport.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (c) setMeta({ title: `${c.name} — KontrataPH`, description: [c.tagline, c.city].filter(Boolean).join(" · ") || undefined });
    return () => resetMeta();
  }, [c]);
  useEffect(() => {
    if (!id) return;
    setStore({ items: [], services: [] });
    api.companyStorefront(id).then(setStore).catch(() => setStore({ items: [], services: [] }));
  }, [id]);

  if (state === "loading") return <DetailLoading label="Loading company…" />;
  if (state === "notfound" || !c) return <DetailNotFound title="Company not found" backHref="/marketplace?tab=Companies" backLabel="Browse Companies" />;

  const company = c as PublicCompany;
  const tags = Array.isArray(company.tags) ? company.tags : [];

  // CTA logic shared by the header buttons and the mobile sticky bar
  const isSelf = !!user && c.ownerUserId === user.id;
  const msgHref = c.hidden || c.ownerClosed ? null : messagesHref(company.ownerUserId, { type: "company", id, name: company.name });
  const quoteHref = c.hidden || c.ownerClosed ? null : messagesHref(company.ownerUserId, { type: "company", id, name: company.name, intent: "quote" });
  const disabledReason = isSelf ? "This is your company" : "Not available on this sample company";
  const go = (target: string | null) => () => { if (!user) return navigate("/login"); if (target) navigate(target); };
  const ctaDisabled = !!user && (isSelf || !msgHref);

  const coverImage = company.coverObject ? `${api.companyCoverUrl(id)}?v=${encodeURIComponent(company.coverObject)}` : companyCoverImage(company.primaryIndustry);
  const companyImages: LightboxImage[] = [
    { src: coverImage, alt: `${company.name} cover image` },
    ...(company.coverImage ? [{ src: api.apiAssetUrl(company.coverImage) || company.coverImage, alt: `${company.name} cover image` }] : []),
    ...(company.logoObject ? [{ src: api.companyLogoUrl(id), alt: `${company.name} logo` }] : []),
  ];
  const openCompanyImage = (src: string) => {
    const index = companyImages.findIndex((image) => image.src === (api.apiAssetUrl(src) || src));
    if (index >= 0) setLightbox({ images: companyImages, index });
  };
  const openListingImage = (src: string, alt: string) => {
    const resolved = api.apiAssetUrl(src);
    if (resolved) setLightbox({ images: [{ src: resolved, alt }], index: 0 });
  };
  const serviceCoverage = [company.serviceCoverage, company.city].filter((value): value is string => typeof value === "string" && value.length > 0);
  const profileSections = [
    { id: "overview", label: t("detail.company.overview"), visible: true },
    { id: "services", label: `${t("detail.company.services")} (${store.services.length})`, visible: store.services.length > 0 },
    { id: "products", label: `${t("detail.company.products")} (${store.items.length})`, visible: store.items.length > 0 },
    { id: "reviews", label: `${t("detail.company.reviews")} (${company.reviews || 0})`, visible: company.reviews > 0 },
  ].filter((section) => section.visible);

  return (
    <>
    <div className="container-x pt-4"><ClosedPostNotice post={c || {}} /></div>
    {isMobileViewport && <CompanyDetailMobile company={company} companyId={id} store={store} isSelf={isSelf} isSignedIn={!!user}
      ctaDisabled={ctaDisabled} disabledReason={disabledReason} onQuote={go(quoteHref)} onContact={go(msgHref)}
      coverSrc={coverImage} onOpenCompanyImage={openCompanyImage} />}
    <div className={`container-x hidden py-6 sm:block ${!isVisible ? "pb-24 lg:pb-6" : ""}`}>
      <DetailBreadcrumb title={company.name} fallback={{ href: "/marketplace?tab=Companies", label: t("detail.company.companies") }} className="mb-5 text-sm sm:text-[17px]" />

      <header className="card overflow-visible p-0">
        <div className="relative h-48 overflow-hidden rounded-t-[inherit] bg-gradient-to-r from-navy via-navy/90 to-brand-red/40">
          <img src={coverImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <button type="button" onClick={() => openCompanyImage(coverImage)} className="absolute inset-0 z-[1] cursor-zoom-in" aria-label={`View ${company.name} cover image`} />
        </div>
        <div className="relative flex flex-wrap items-start gap-4 px-5 pb-5 pt-5 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto]">
          <div className="relative -mt-[68px] h-24 w-24 shrink-0 overflow-hidden rounded-full bg-navy ring-4 ring-white">
            {company.logoObject
              ? <button type="button" onClick={() => openCompanyImage(api.companyLogoUrl(id))} className="h-full w-full cursor-zoom-in" aria-label={`View ${company.name} logo`}><img src={api.companyLogoUrl(id)} alt={company.name} className="h-full w-full object-cover" /></button>
              : <div className="grid h-full w-full place-items-center bg-navy text-3xl font-extrabold text-white">{company.initials}</div>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h1 className="text-2xl font-extrabold leading-tight text-navy">{company.name}</h1>
            </div>
            {(company.subtype || company.primaryIndustry) && <p className="mt-1 font-semibold text-mut">{company.subtype || company.primaryIndustry}</p>}
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-mut">
              {(company.rating > 0 || company.reviews > 0) && <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-brand-gold text-brand-gold" /><b className="text-navy">{company.rating ? company.rating.toFixed(1) : "New"}</b>{company.reviews > 0 && `(${company.reviews} reviews)`}</span>}
              {company.since && <span>Member since {company.since}</span>}
            </div>
            <div className="mt-1 flex items-center gap-1 text-[11px] text-mut"><LocationPin className="h-3.5 w-3.5 shrink-0" />{company.city}</div>
          </div>
          <div ref={actionRef} className="flex w-full flex-wrap gap-2 lg:ml-auto lg:w-auto lg:flex-nowrap lg:justify-end">
            {isSelf && <Link href={`/company/${id}/edit`} className="btn-red">Edit business profile</Link>}
            {isSelf && <Link href={`/company/${id}/licenses`} className="btn-outline">Licenses &amp; verification</Link>}
            {!isSelf && (user
              ? <SaveButton kind="company" refId={id} label={t("common.save", "Save")} />
              : <button onClick={() => navigate("/login")} className="btn-outline">{t("common.save", "Save")}</button>)}
            {!isSelf && <><button onClick={go(msgHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
              className="btn-outline disabled:cursor-not-allowed disabled:opacity-50"><MessageSquare className="h-3.5 w-3.5" />{t("detail.company.contact")}</button>
            <button onClick={go(quoteHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
              className="btn-red disabled:cursor-not-allowed disabled:opacity-50"><ReceiptText className="h-3.5 w-3.5" />{t("detail.company.requestQuote")}</button></>}
          </div>
        </div>
        <nav className="flex overflow-x-auto border-t border-line px-1" aria-label={t("company.profile")}>
          {profileSections.map(({ id: section, label }) => (
            <a key={section} href={`#${section}`} onClick={() => setActiveSection(section)} className={`shrink-0 border-b-2 px-4 py-2.5 text-xs ${activeSection === section ? "border-brand-red text-brand-red" : "border-transparent text-mut hover:text-navy"}`}>{label}</a>
          ))}
        </nav>
      </header>

      <div className="mt-4 grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[240px_minmax(0,1fr)_260px]">
        <aside className="min-w-0 space-y-4 lg:sticky lg:top-24 lg:self-start"><BusinessProfileCard c={company} /></aside>
        <main className="min-w-0 space-y-4">
          <section id="overview" className="card scroll-mt-24 p-5">
            <h2 className="text-sm font-bold text-navy">About {company.name}</h2>
            <p className="mt-3 whitespace-pre-line text-sm leading-6 text-mut">{company.tagline || "Learn more about this company and the services and products it offers."}</p>
          </section>

          {store.services.length > 0 && <section id="services" className="card scroll-mt-24 p-5">
            <SectionHeading title="Services" count={store.services.length} expanded={showAllServices} onToggle={() => setShowAllServices((current) => !current)} />
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
              {(showAllServices ? store.services : store.services.slice(0, 6)).map((service) => <CompanyServiceCard key={service.id} service={service} ownerUserId={company.ownerUserId} onQuote={go} onOpenImage={openListingImage} />)}
            </div>
          </section>}

          {store.items.length > 0 && <section id="products" className="card scroll-mt-24 p-5">
            <SectionHeading title={t("detail.company.productsEquipment")} count={store.items.length} expanded={showAllItems} onToggle={() => setShowAllItems((current) => !current)} />
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
              {(showAllItems ? store.items : store.items.slice(0, 6)).map((item) => <CompanyItemCard key={item.id} item={item} onOpenImage={openListingImage} />)}
            </div>
          </section>}

          {(company.rating > 0 || company.reviews > 0) && <section id="reviews" className="card scroll-mt-24 p-5">
            <h2 className="text-sm font-bold text-navy">{t("detail.company.reviewsRatings")}</h2>
            <div className="mt-3 flex items-center gap-4">
              <div><div className="text-3xl font-extrabold leading-none text-navy">{company.rating ? company.rating.toFixed(1) : "—"}</div>{company.rating > 0 && <Rating value={company.rating} />}<div className="mt-1 text-[11px] text-mut">{company.reviews || 0} reviews</div></div>
              <p className="border-l border-line pl-4 text-xs text-mut">{t("detail.company.ratingsBasis")}</p>
            </div>
          </section>}
        </main>

        <aside className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-24 lg:self-start">
          {serviceCoverage.length > 0 && <section className="rounded-xl border border-line bg-white p-4 shadow-sm">
            <h2 className="text-sm font-bold text-navy">{t("detail.company.businessArea")}</h2>
            <div className="relative mt-2 flex h-24 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-sky-100 via-blue-50 to-emerald-100">
              <div aria-hidden="true" className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(32deg, transparent 46%, white 47% 50%, transparent 51%), linear-gradient(125deg, transparent 46%, #b9d4df 47% 48%, transparent 49%), linear-gradient(82deg, transparent 48%, white 49% 52%, transparent 53%)" }} />
              <div className="z-10 grid h-14 w-14 place-items-center rounded-full border border-blue-300 bg-blue-100/60"><Building2 className="h-5 w-5 text-blue-700" /></div>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-mut"><MapPin className="h-3.5 w-3.5 text-brand-red" />{t("detail.company.serves")} <b className="text-navy">{serviceCoverage.join(" · ")}</b></p>
          </section>}

          {tags.length > 0 && <section className="rounded-xl border border-line bg-white p-4 shadow-sm">
            <h2 className="text-sm font-bold text-navy">{t("detail.company.areasExpertise")}</h2>
            <div className="mt-2 flex flex-wrap gap-1.5">{tags.map((tag) => <span key={tag} className="rounded-full bg-navy-50 px-2.5 py-1 text-[10px] text-navy-700">{tag}</span>)}</div>
          </section>}

          <SimilarOrganizations id={id} />
        </aside>
      </div>

      {!isSelf && !isVisible && <MobileActionBar>
        <button onClick={go(msgHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
          className="btn-outline flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><MessageSquare className="w-4 h-4" />{t("detail.company.contact")}</button>
        <button onClick={go(quoteHref)} disabled={ctaDisabled} title={ctaDisabled ? disabledReason : undefined}
          className="btn-red flex-1 disabled:opacity-50 disabled:cursor-not-allowed"><ReceiptText className="w-4 h-4" />{t("detail.company.requestQuote")}</button>
      </MobileActionBar>}
    </div>
    {lightbox && <ImageLightbox images={lightbox.images} initialIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </>
  );
}

function SectionHeading({ title, count, expanded, onToggle }: { title: string; count: number; expanded: boolean; onToggle: () => void }) {
  const { t } = useI18n();
  return <div className="mb-2.5 flex items-center justify-between"><h2 className="text-sm font-bold text-navy">{title} ({count})</h2>{count > 6 && <button type="button" onClick={onToggle} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">{t(expanded ? "common.showLess" : "common.viewAll")} <ArrowRight className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-[-90deg]" : ""}`} /></button>}</div>;
}

function CompanyServiceCard({ service, ownerUserId, onQuote, onOpenImage }: {
  service: CompanyStoreService;
  ownerUserId?: number | null;
  onQuote: (target: string | null) => () => void;
  onOpenImage: (src: string, alt: string) => void;
}) {
  const { t } = useI18n();
  const quoteHref = messagesHref(ownerUserId, { type: "service", id: service.id, name: service.title, intent: "quote" });
  return (
    <article className="overflow-hidden rounded-lg border border-line bg-white">
      <div className="relative block aspect-[16/9] overflow-hidden bg-navy-50">
        <Link href={`/s/${service.id}`} className="block h-full w-full"><CoverArt src={service.coverImage} alt={service.title} Icon={Wrench} /></Link>
        {service.coverImage && <button type="button" onClick={() => onOpenImage(service.coverImage || "", service.title)} className="absolute inset-0 cursor-zoom-in" aria-label={`View ${service.title} image`} />}
      </div>
      <div className="p-2">
        <div className="flex items-start justify-between gap-1">
          <Link href={`/s/${service.id}`} className="min-w-0 truncate text-xs font-bold text-navy hover:text-brand-red">{service.title}</Link>
          {(service.reviews ?? 0) > 0 && typeof service.rating === "number" && <span className="shrink-0 text-[10px] text-navy"><Star className="mr-0.5 inline h-3 w-3 fill-brand-gold text-brand-gold" />{service.rating.toFixed(1)} ({service.reviews})</span>}
        </div>
        {service.tags.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{service.tags.slice(0, 3).map((tag) => <span key={tag} className="rounded-full bg-navy-50 px-2 py-0.5 text-[9px] text-navy-700">{tag}</span>)}</div>}
        <div className="mt-2 flex items-end justify-between gap-1 border-t border-line pt-2">
          <div><span className="block text-[9px] text-mut">{t("detail.company.startingAt")}</span><span className="text-xs font-bold text-navy">{peso(service.from)}</span></div>
          <button type="button" onClick={onQuote(quoteHref)} className="btn-red px-2 py-1.5 text-[9px]">{t("detail.company.requestQuote")}</button>
        </div>
      </div>
    </article>
  );
}

function CompanyItemCard({ item, onOpenImage }: { item: CompanyStoreItem; onOpenImage: (src: string, alt: string) => void }) {
  const { t } = useI18n();
  const Icon = item.kind === "equipment" ? Wrench : Package;
  const isRent = item.mode === "rent";
  return (
    <article className="overflow-hidden rounded-lg border border-line bg-white">
      <div className="relative block aspect-[16/9] overflow-hidden bg-navy-50">
        <Link href={`/item/${item.id}`} className="block h-full w-full"><CoverArt src={item.imageUrl || item.coverImage} alt={item.title} Icon={Icon} /></Link>
        {(item.imageUrl || item.coverImage) && <button type="button" onClick={() => onOpenImage(item.imageUrl || item.coverImage || "", item.title)} className="absolute inset-0 cursor-zoom-in" aria-label={`View ${item.title} image`} />}
      </div>
      <div className="p-2">
        <Link href={`/item/${item.id}`} className="block truncate text-xs font-bold text-navy hover:text-brand-red">{item.title}</Link>
        <div className="mt-1.5 flex flex-wrap gap-1">
          <span className={`rounded-full px-2 py-0.5 text-[9px] ${isRent ? "bg-brand-green/10 text-brand-greendark" : "bg-navy-50 text-navy-700"}`}>{isRent ? "For Rent" : "For Sale"}</span>
          {item.condition && <span className="rounded-full bg-navy-50 px-2 py-0.5 text-[9px] capitalize text-navy-700">{item.condition}</span>}
        </div>
        <div className="mt-2 flex items-end justify-between gap-1 border-t border-line pt-2">
          <span className="text-xs font-bold text-navy">{peso(item.price)}{isRent ? ` / ${item.unit || "day"}` : ""}</span>
          <Link href={`/item/${item.id}`} className="btn-outline px-2 py-1.5 text-[9px]">{t("detail.company.inquire")}</Link>
        </div>
      </div>
    </article>
  );
}

/** Renders only company fields that have been entered in the public profile. */
function BusinessProfileCard({ c }: { c: PublicCompany }) {
  const { t } = useI18n();
  const roles = c.businessRoles ?? [];
  const ownership = c.ownershipClassifications ?? [];
  const enterprise = c.enterpriseClassifications ?? [];
  const secondary = c.secondaryIndustries ?? [];
  const products = c.productsServices ?? [];
  const markets = c.customerMarkets ?? [];
  const facts = ([
    ["Type", c.subtype],
    ["Industry", c.primaryIndustry],
    ["PSIC", c.psicCode],
    ["Business status", c.operatingStatus],
    ["Ownership", ownership.join(", ") || undefined],
    ["Enterprise class", enterprise.join(", ") || undefined],
    ["Corporate relationship", c.corporateRelationship],
    ["Service coverage", c.serviceCoverage],
  ] as [string, string | null | undefined][]).filter(([, value]) => typeof value === "string" && value.trim().length > 0);
  const has = facts.length > 0 || roles.length > 0 || secondary.length > 0 || products.length > 0 || markets.length > 0;
  if (!has) return null;

  const listRow = (label: string, items: string[]) => items.length > 0 && (
    <div className="mt-3">
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-mut">{label}</div>
      <ul className="list-disc space-y-1.5 pl-4 text-xs leading-5 text-navy-700">{items.map((item) => <li key={item} className="break-words">{item}</li>)}</ul>
    </div>
  );

  return (
    <section className="rounded-xl border border-line bg-white p-4 shadow-sm">
      <h2 className="text-sm font-bold text-navy">{t("detail.company.information")}</h2>
      {facts.length > 0 && <dl className="mt-3 grid gap-3">
        {facts.map(([label, value]) => <div key={label} className="space-y-1 border-b border-line/70 pb-3 text-xs">
          <dt className="shrink-0 text-mut">{label}</dt><dd className="font-medium text-navy">{value}</dd>
        </div>)}
      </dl>}
      {listRow("Business roles", roles)}
      {listRow("Secondary industries", secondary)}
      {listRow("Products & services", products)}
      {listRow("Customer markets", markets)}
    </section>
  );
}

/** v3.9.0 Phase 6 — taxonomy-matched organizations, shown when there are any. */
function SimilarOrganizations({ id }: { id: string }) {
  const { t } = useI18n();
  const [items, setItems] = useState<any[] | null>(null);
  useEffect(() => {
    let alive = true;
    api.similarCompanies(id).then((r) => { if (alive) setItems(r); }).catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [id]);
  if (!items || items.length === 0) return null;
  return (
    <section className="rounded-xl border border-line bg-white p-4 shadow-sm">
      <h2 className="mb-2.5 text-sm font-bold text-navy">{t("detail.company.similarCompanies")}</h2>
      <div className="grid grid-cols-2 gap-2">
        {items.slice(0, 2).map((c) => (
          <Link key={c.id} href={`/c/${c.id}`} className="min-w-0 rounded-lg border border-line p-2 transition hover:border-navy">
            <div className="flex items-center gap-1.5">
              <div className="grid h-7 w-7 shrink-0 place-items-center rounded bg-navy text-[9px] font-bold text-white">{c.initials}</div>
              <span className="line-clamp-2 min-w-0 text-[10px] font-semibold text-navy">{c.name}</span>
            </div>
            <div className="mt-1 truncate text-[9px] text-mut"><Star className="mr-0.5 inline h-2.5 w-2.5 fill-brand-gold text-brand-gold" />{c.rating?.toFixed?.(1) || "—"} · {c.city}</div>
            <span className="mt-1.5 inline-flex w-full items-center justify-center gap-1 rounded-md border border-line py-1 text-[9px] font-medium text-navy"><Users className="h-3 w-3" />{t("detail.company.viewProfile")}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
